import moment from 'moment'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { CreateOrgBankTransferPdfApiArg, useCreateOrgBankTransferPdfMutation } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { downloadBlob } from '@/app/service/util/download'
import { useAppDispatch } from '@/app/store/redux-hooks'

type Payload = CreateOrgBankTransferPdfApiArg['body']

export const useCreateProformaInvoice = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [isLoading, setIsLoading] = useState(false)
  const [mutate] = useCreateOrgBankTransferPdfMutation()

  const createProforma = useCallback(
    async (body: Payload) => {
      if (!orgId) return false
      setIsLoading(true)
      try {
        const blob = await mutate({ orgId: orgId.toString(), body }).unwrap()
        const datePart = new Date(body.date).toISOString().slice(0, 10)
        const fallback = `proform-invoice-${moment(datePart).format('YYYY/MM/DD')}.pdf`
        downloadBlob(blob as Blob, fallback)
        return true
      } catch {
        dispatch(
          createNotification({
            'create-proforma-invoice-failure': {
              type: 'error',
              message: 'Unable to download your proforma invoice. Please try again later.',
            },
          }),
        )
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [orgId, dispatch, mutate],
  )

  return [createProforma, { isLoading }] as const
}
